<script lang="ts" setup>
import { computed, onBeforeMount, ref } from "vue";
import {
  createReviewerPetition,
  readReviewerPetitionPage,
} from "@/apis/reviewer";
import { useCoinRank } from "@/stores/coinRank";
import moment from "moment-timezone";
import { showLoadingToast, showSuccessToast } from "vant";
import { useI18n } from "vue-i18n";
import { useGlobalUser } from "@/stores/globalUser";
import { useReviewer } from "@/stores/reviewer";

const user = useGlobalUser();
const { t } = useI18n();
const rankStore = useCoinRank();

const message = ref("");
const submitStatus = ref<"pending" | "approved" | "rejected" | "none">("none");
const loading = ref(false);
const showSubmitForm = computed(() => {
  return submitStatus.value === "none" || submitStatus.value === "rejected";
});

const getApplicationList = async () => {
  try {
    loading.value = true;
    if (user.isReviewer) {
      submitStatus.value = "approved";
      return;
    }
    const res = await readReviewerPetitionPage({
      pagination: { page: 1, perPage: 5 },
      sort: { field: "id", order: "desc" },
    });
    const data = res.page.data;
    // 1. 没有提交过申请
    if (data.length === 0) {
      submitStatus.value = "none";
      return;
    }
    // 2. 提交过申请
    const application = data[0];
    const isExpiredApplication = moment(application.dtCreate).isBefore(
      moment(rankStore.currentCoinRankDtStart)
    );
    // 2.1 提交过申请但是已经过期
    if (isExpiredApplication) {
      submitStatus.value = "none";
    } else if (!application.dtReject && !application.dtApprove) {
      submitStatus.value = "pending";
    } else {
      submitStatus.value = "rejected";
    }
  } finally {
    loading.value = false;
  }
};

onBeforeMount(() => {
  getApplicationList();
});

const onSubmit = async () => {
  const loading = showLoadingToast("");
  try {
    await createReviewerPetition(message.value);
    submitStatus.value = "pending";
    loading.close();
    showSuccessToast(t("Message.submissionSuccessful"));
  } finally {
    loading.close();
  }
};
</script>
<template>
  <div class="h-full grid grid-rows-[auto_1fr_auto] relative">
    <threads-nav show-back>
      <template #center>
        <div>{{ $t("Coin.applySeniorReader") }}</div>
      </template>
      <template #right>
        <button
          v-if="!loading && showSubmitForm"
          :disabled="!message.length"
          class="btn btn-sm btn-primary rounded"
          @click="onSubmit"
        >
          {{ $t("Buttons.submit") }}
        </button>
      </template>
    </threads-nav>
    <div
      v-if="loading"
      class="h-full flex flex-col items-center justify-center"
    >
      <span class="loading"></span>
    </div>
    <div v-else-if="showSubmitForm" class="h-full p-4 flex flex-col gap-2">
      <div class="text-base flex gap-1 text-base-content/50">
        {{ $t("Coin.reasonForApplication") }}
        <span class="text-primary">*</span>
      </div>
      <van-field
        v-model="message"
        :placeholder="$t('Coin.bodyOfText')"
        autosize
        class="bg-base-100 border rounded"
        label-align="top"
        rows="5"
        type="textarea"
      />
      <article
        class="prose prose-sm md:prose-base"
        v-html="$t('Coin.seniorReaderRules')"
      />
    </div>
    <div
      v-else-if="submitStatus === 'pending'"
      class="h-full flex flex-col gap-8 justify-center items-center p-10"
    >
      <div>
        <i class="iconfont icon-mail-bulk text-[4rem] text-base-content/50" />
      </div>
      <div class="text-lg text-base-content/50 text-center w-">
        {{ $t("Coin.applicationSubmitted") }}
      </div>
    </div>
    <div
      v-else-if="submitStatus === 'approved'"
      class="h-full flex flex-col gap-8 justify-center items-center p-10"
    >
      <div>
        <i class="iconfont icon-confirm text-[4rem] text-base-content/50" />
      </div>
      <div class="text-lg text-base-content/50 text-center w-">
        {{ $t("Coin.applicationApproved") }}
      </div>
    </div>
  </div>
</template>
